<template>
  <div class="space-y-6">

    <!-- Erreur de la dernière action : jamais silencieuse (avant : un console.error seul). -->
    <div v-if="erreur" class="flex items-start justify-between gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
      <span>{{ erreur }}</span>
      <button type="button" @click="erreur = ''" class="text-red-500 hover:text-red-700" :aria-label="$t('commun.fermer')">✕</button>
    </div>

    <!-- En-tête : activation, fuseau, état d'envoi -->
    <div class="bg-white rounded-lg shadow p-4 space-y-3">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div class="text-sm font-medium text-gray-700 mb-1">{{ $t('planning.activationQ') }}</div>
          <!-- Même règle que tout réglage à deux états : l'option cochée est ce que la carte
               RAPPORTE ; une demande pas encore appliquée est « en attente de la carte ». -->
          <choix-binaire v-if="modifiable" :titre="$t('planning.activationQ')" :busy="activationEnCours" :send="basculer"
                         :rapporte="config.schedule_enabled_rapporte" :demande="config.schedule_enabled"
                         :label-oui="$t('planning.optionActif')" :label-non="$t('planning.optionInactif')"
                         cmd-oui="schedule_enable" cmd-non="schedule_disable" />
          <div v-else class="text-sm font-semibold text-gray-800">
            {{ config.schedule_enabled ? $t('planning.planningActive') : $t('planning.planningDesactive') }}
          </div>
          <p v-if="config.activation_en_attente" class="mt-1 text-xs text-amber-700">{{ $t('planning.enVeille') }}</p>
        </div>
        <div>
          <label for="fuseau" class="block text-xs text-gray-500 mb-1">{{ $t('planning.fuseauHoraire') }}</label>
          <select id="fuseau" v-model="config.timezone" @change="saveConfig" :disabled="!modifiable"
                  class="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100">
            <option v-for="tz in timezones" :key="tz.value" :value="tz.value">{{ tz.label }}</option>
          </select>
        </div>
      </div>

      <!-- Où en est l'envoi : brouillon non envoyé → envoyé, en attente → appliqué. -->
      <div v-if="config.modifications_non_envoyees"
           class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        {{ $t('planning.nonEnvoye') }}
      </div>
      <div v-else-if="config.planning_en_attente"
           class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        {{ $t('planning.enAttenteCarte') }}
      </div>
      <div class="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
        <i18n-t keypath="planning.dernierEnvoi" tag="span"><template #date><span class="font-medium text-gray-700">{{ lastSentLabel }}</span></template></i18n-t>
        <span v-if="config.applique_le && !config.planning_en_attente">{{ $t('planning.appliqueLe', { date: formatIso(config.applique_le) }) }}</span>
        <span v-if="config.last_next_at && config.last_next_at > 0" class="text-primary-600">
          {{ $t('planning.prochaine', { date: formatEpoch(config.last_next_at) }) }}
        </span>
      </div>
      <p class="text-xs text-gray-400">{{ $t('planning.heuresFuseau', { fuseau: nomFuseau }) }}</p>
    </div>

    <!-- Planning hebdomadaire : une carte par jour -->
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      <div v-for="day in days" :key="day.id" class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center justify-between mb-3">
          <h4 class="font-semibold text-gray-800">{{ day.label }}</h4>
          <button v-if="modifiable" @click="openAddSlot(day.id)"
                  class="text-xs px-2 py-1 rounded bg-primary-50 text-primary-600 hover:bg-primary-100 font-medium">
            {{ $t('planning.ajouterPlus') }}
          </button>
        </div>

        <!-- Liste des transitions du jour -->
        <div v-if="slotsForDay(day.id).length === 0" class="text-xs text-gray-400 italic">
          {{ $t('planning.aucuneTransitionJour') }}
        </div>
        <div v-else class="space-y-2">
          <div v-for="slot in slotsForDay(day.id)" :key="slot.id"
               :class="`flex items-center justify-between px-3 py-2 rounded-lg text-sm
                        ${slot.enabled
                          ? (slot.action === 1 ? 'bg-orange-50' : 'bg-green-50')
                          : 'bg-gray-50 opacity-60'}`">
            <div class="flex items-center gap-2">
              <span class="text-base">{{ slot.action === 1 ? '🔒' : '🔓' }}</span>
              <span :class="`font-mono font-semibold ${slot.action === 1 ? 'text-orange-700' : 'text-green-700'}`">
                {{ slot.time }}
              </span>
              <span :class="`text-xs ${slot.action === 1 ? 'text-orange-500' : 'text-green-500'}`">
                {{ slot.action === 1 ? $t('planning.armement') : $t('planning.desarmement') }}
              </span>
            </div>
            <div v-if="modifiable" class="flex items-center gap-2">
              <input type="checkbox" :checked="slot.enabled"
                     @change="toggleSlot(slot)"
                     class="rounded text-primary-600 cursor-pointer" />
              <button @click="removeSlot(slot.id)" class="text-red-400 hover:text-red-600" :aria-label="$t('planning.supprimer')">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Aperçu des prochaines transitions -->
    <div class="bg-white rounded-lg shadow p-4">
      <div class="flex items-center justify-between mb-3">
        <div>
          <h3 class="font-semibold text-gray-800">{{ $t('planning.apercu') }}</h3>
          <p class="text-xs text-gray-400">{{ $t('planning.aideApercu') }}</p>
        </div>
        <button @click="loadPreview" :disabled="previewLoading"
                class="text-xs text-primary-600 hover:underline disabled:opacity-50">
          {{ $t('planning.calculer') }}
        </button>
      </div>
      <div v-if="previewLoading" class="text-center py-4 text-gray-400 text-sm">{{ $t('planning.calculEnCours') }}</div>
      <div v-else-if="preview.length === 0" class="text-sm text-gray-400 italic">
        {{ $t('planning.aucuneTransitionAVenir') }}
      </div>
      <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div v-for="(tr, i) in preview" :key="i"
             :class="`flex items-center gap-2 text-sm px-3 py-2 rounded
                      ${tr.a === 1 ? 'bg-orange-50 text-orange-700' : 'bg-green-50 text-green-700'}`">
          <span>{{ tr.a === 1 ? '🔒' : '🔓' }}</span>
          <span class="font-mono text-xs">{{ formatEpoch(tr.t) }}</span>
        </div>
      </div>
      <div v-if="preview.length" class="text-xs text-gray-400 mt-2">{{ $t('planning.nbTransitions', { n: preview.length }) }}</div>
    </div>

    <!-- Exceptions -->
    <div class="bg-white rounded-lg shadow p-4">
      <div class="flex items-center justify-between mb-4">
        <h3 class="font-semibold text-gray-800">{{ $t('planning.exceptions') }}</h3>
        <button v-if="modifiable" @click="showAddException = true"
                class="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700">
          {{ $t('planning.ajouterPlus') }}
        </button>
      </div>
      <div v-if="exceptions.length === 0" class="text-sm text-gray-400 italic">{{ $t('planning.aucuneException') }}</div>
      <div v-else class="space-y-2">
        <div v-for="exc in exceptions" :key="exc.id"
             class="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 text-sm">
          <div class="space-y-0.5">
            <div class="font-medium text-gray-800">{{ exc.label || $t('planning.exception') }}</div>
            <div class="text-xs text-gray-500 font-mono">{{ exc.date_from }} → {{ exc.date_to }}</div>
          </div>
          <div class="flex items-center gap-3">
            <span :class="modeBadge(exc.mode)">{{ modeLabel(exc.mode) }}</span>
            <span v-if="exc.mode === 'custom'" class="text-xs text-gray-500 font-mono">
              {{ exc.arm_time || '—' }} / {{ exc.disarm_time || '—' }}
            </span>
            <button v-if="modifiable" @click="removeException(exc.id)"
                    class="text-red-400 hover:text-red-600 text-xs">{{ $t('planning.supprimer') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Bouton d'envoi — ouvert au client le 28/09 : le renvoi forcé conserve le numéro de
         séquence (schedule.service.js), il n'a donc rien de destructeur. -->
    <div v-if="modifiable" class="flex justify-end gap-3">
      <button @click="sendToMcu" :disabled="sending"
              class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-medium">
        {{ sending ? $t('planning.envoiEnCours') : $t('planning.envoyer') }}
      </button>
    </div>
    <div v-if="sendResult" :class="`text-sm p-3 rounded ${sendResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`">
      {{ sendResult.texte }}
    </div>

    <!-- Modal ajout transition -->
    <div v-if="addSlotModal.open" class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl shadow-xl p-6 w-full max-w-xs mx-4">
        <h4 class="font-semibold text-gray-800 mb-4">
          {{ $t('planning.nouvelleTransition', { jour: days.find(d => d.id === addSlotModal.day)?.label }) }}
        </h4>
        <div class="space-y-3">
          <div>
            <label class="block text-xs text-gray-500 mb-1">{{ $t('planning.heure') }}</label>
            <input v-model="addSlotModal.time" type="time"
                   class="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono" />
          </div>
          <div>
            <label class="block text-xs text-gray-500 mb-1">{{ $t('planning.action') }}</label>
            <div class="flex gap-2">
              <button @click="addSlotModal.action = 1"
                      :class="`flex-1 py-2 rounded text-sm font-medium border transition-colors
                               ${addSlotModal.action === 1
                                 ? 'bg-orange-100 border-orange-400 text-orange-700'
                                 : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`">
                🔒 {{ $t('planning.armer') }}
              </button>
              <button @click="addSlotModal.action = 0"
                      :class="`flex-1 py-2 rounded text-sm font-medium border transition-colors
                               ${addSlotModal.action === 0
                                 ? 'bg-green-100 border-green-400 text-green-700'
                                 : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`">
                🔓 {{ $t('planning.desarmer') }}
              </button>
            </div>
          </div>
        </div>
        <div v-if="addSlotModal.error" class="mt-3 text-xs text-red-600 bg-red-50 rounded p-2">
          {{ addSlotModal.error }}
        </div>
        <div class="flex justify-end gap-3 mt-5">
          <button @click="addSlotModal.open = false"
                  class="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">{{ $t('commun.annuler') }}</button>
          <button @click="confirmAddSlot" :disabled="!addSlotModal.time || addSlotModal.loading"
                  class="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50">
            {{ addSlotModal.loading ? $t('planning.envoi') : $t('planning.ajouter') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal ajout exception -->
    <div v-if="showAddException" class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
        <h4 class="font-semibold text-gray-800 mb-4">{{ $t('planning.nouvelleException') }}</h4>
        <div class="space-y-3">
          <div>
            <label class="block text-xs text-gray-500 mb-1">{{ $t('planning.libelleOptionnel') }}</label>
            <input v-model="newExc.label" type="text" :placeholder="$t('planning.exempleLibelle')"
                   class="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs text-gray-500 mb-1">{{ $t('planning.dateDebut') }}</label>
              <input v-model="newExc.date_from" type="date"
                     class="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label class="block text-xs text-gray-500 mb-1">{{ $t('planning.dateFin') }}</label>
              <input v-model="newExc.date_to" type="date"
                     class="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label class="block text-xs text-gray-500 mb-1">{{ $t('planning.comportement') }}</label>
            <select v-model="newExc.mode"
                    class="w-full border border-gray-300 rounded px-3 py-2 text-sm">
              <option value="armed_all_day">{{ $t('planning.optionArmeJournee') }}</option>
              <option value="disarmed_all_day">{{ $t('planning.optionDesarmeJournee') }}</option>
              <option value="custom">{{ $t('planning.optionPersonnalise') }}</option>
              <option value="none">{{ $t('planning.optionAucune') }}</option>
            </select>
          </div>
          <div v-if="newExc.mode === 'custom'" class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs text-orange-600 mb-1">{{ $t('planning.armement') }}</label>
              <input v-model="newExc.arm_time" type="time"
                     class="w-full border border-gray-300 rounded px-3 py-2 text-sm text-orange-700" />
            </div>
            <div>
              <label class="block text-xs text-green-600 mb-1">{{ $t('planning.desarmement') }}</label>
              <input v-model="newExc.disarm_time" type="time"
                     class="w-full border border-gray-300 rounded px-3 py-2 text-sm text-green-700" />
            </div>
          </div>
        </div>
        <div v-if="erreurException" class="mt-3 text-xs text-red-600 bg-red-50 rounded p-2">{{ erreurException }}</div>
        <div class="flex justify-end gap-3 mt-5">
          <button @click="showAddException = false; resetNewExc()"
                  class="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">{{ $t('commun.annuler') }}</button>
          <button @click="addException" :disabled="!newExc.date_from || !newExc.date_to || !newExc.mode"
                  class="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50">
            {{ $t('planning.ajouter') }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import apiService from '@/services/api.service';
import socketService from '@/services/socket.service';
import { useI18n } from 'vue-i18n';
import { texteErreur, texteSucces } from '@/i18n/serveur';
import { localeIntl } from '@/i18n/dates';
import ChoixBinaire from '@/components/device/commands/ChoixBinaire.vue';

// `label` = clé de traduction (planning.jours.* / planning.fuseaux.*), résolue dans setup().
const DAYS = [
  { id: 0, label: 'lundi' },
  { id: 1, label: 'mardi' },
  { id: 2, label: 'mercredi' },
  { id: 3, label: 'jeudi' },
  { id: 4, label: 'vendredi' },
  { id: 5, label: 'samedi' },
  { id: 6, label: 'dimanche' }
];

// Fuseaux proposés au client : ceux où l'on installe la carte. Jamais UTC ni un décalage fixe
// (décision du 02/10/2026) : un planning suit l'heure de la vie du client, heure d'été comprise.
// Un fuseau posé par l'admin hors de cette liste reste affiché tel quel.
const TIMEZONES = [
  { value: 'Europe/Paris',        label: 'paris' },
  { value: 'America/Guadeloupe',  label: 'guadeloupe' },
  { value: 'America/Martinique',  label: 'martinique' },
  { value: 'America/Cayenne',     label: 'guyane' },
  { value: 'Indian/Reunion',      label: 'reunion' },
  { value: 'Indian/Mayotte',      label: 'mayotte' },
  { value: 'Pacific/Noumea',      label: 'nouvelleCaledonie' },
  { value: 'Pacific/Tahiti',      label: 'tahiti' },
  { value: 'Europe/Brussels',     label: 'belgique' },
  { value: 'Europe/Zurich',       label: 'suisse' },
  { value: 'Europe/Luxembourg',   label: 'luxembourg' },
  { value: 'Europe/Rome',         label: 'italie' },
  { value: 'Europe/Madrid',       label: 'espagne' },
  { value: 'Europe/Berlin',       label: 'allemagne' },
  { value: 'Europe/London',       label: 'royaumeUni' }
];

export default {
  name: 'DeviceSchedule',
  components: { ChoixBinaire },
  props: {
    deviceId: { type: Number, required: true },
    // Deux notions distinctes, longtemps confondues sous le seul `isAdmin` :
    //
    //   `modifiable` — a-t-on le droit de composer ce planning ? Le client l'a sur SES
    //     appareils : le planning d'armement décrit ses horaires d'activité, lui seul les
    //     connaît. Tant que ce drapeau était lié à `isAdmin`, le client voyait un planning
    //     qu'il ne pouvait pas remplir, tout en pouvant l'allumer par l'interrupteur de
    //     l'en-tête — qui, lui, n'était pas masqué et échouait en silence.
    //
    //   `isAdmin` — réservait le bouton « Envoyer », ouvert au client le 28/09 (le renvoi
    //     forcé conserve le seq : rien de destructeur). Il ne commande plus rien d'autre.
    //
    // `modifiable` est à `false` par défaut : une vue nouvelle est en lecture seule par
    // oubli, jamais modifiable par oubli.
    isAdmin:    { type: Boolean, default: false },
    modifiable: { type: Boolean, default: false }
  },
  setup(props) {
    const { t } = useI18n();
    const config     = ref({ timezone: 'Europe/Paris', schedule_enabled: 0 });
    const erreur     = ref('');
    const erreurException = ref('');
    const activationEnCours = ref(false);
    const slots      = ref([]);
    const exceptions = ref([]);
    const preview    = ref([]);
    const previewLoading = ref(false);
    const sending    = ref(false);
    const sendResult = ref(null);
    const showAddException = ref(false);
    const newExc = ref({ label: '', date_from: '', date_to: '', mode: 'armed_all_day', arm_time: '', disarm_time: '' });
    const addSlotModal = ref({ open: false, day: 0, time: '', action: 1, loading: false, error: '' });

    const days      = computed(() => DAYS.map(d => ({ ...d, label: t(`planning.jours.${d.label}`) })));
    const timezones = computed(() => {
      const liste = TIMEZONES.map(z => ({ ...z, label: t(`planning.fuseaux.${z.label}`) }));
      const tz = config.value.timezone;
      return tz && !liste.some(z => z.value === tz) ? [{ value: tz, label: tz }, ...liste] : liste;
    });
    const nomFuseau = computed(() => (timezones.value.find(z => z.value === config.value.timezone) || {}).label
      || config.value.timezone || 'Europe/Paris');
    // Toute action : en cas d'échec, le message du serveur (traduit) s'affiche en tête.
    const echec = (e) => { erreur.value = texteErreur(e, t('planning.erreurInconnue')); };

    function slotsForDay(dayId) {
      return slots.value
        .filter(s => s.day_of_week === dayId)
        .sort((a, b) => a.time.localeCompare(b.time));
    }

    async function loadSchedule() {
      try {
        const r = await apiService.getSchedule(props.deviceId);
        config.value     = r.data.config     || { timezone: 'Europe/Paris', schedule_enabled: 0 };
        slots.value      = r.data.slots      || [];
        exceptions.value = r.data.exceptions || [];
      } catch (e) { echec(e); }
    }
    // Après chaque modification du brouillon : l'en-tête doit dire « pas encore envoyé ».
    const rafraichirEtat = async () => {
      try { config.value = (await apiService.getSchedule(props.deviceId)).data.config || config.value; }
      catch { /* l'action elle-même a réussi : rien à signaler */ }
    };

    async function saveConfig() {
      try {
        const r = await apiService.updateScheduleConfig(props.deviceId, { timezone: config.value.timezone });
        config.value = r.data;
      } catch (e) { echec(e); await loadSchedule(); }
    }

    // Appelé par ChoixBinaire avec la commande correspondant à l'option choisie.
    async function basculer(cmd) {
      activationEnCours.value = true;
      try {
        const r = await apiService.updateScheduleConfig(props.deviceId, { schedule_enabled: cmd === 'schedule_enable' });
        config.value = r.data;
      } catch (e) { echec(e); }
      finally { activationEnCours.value = false; }
    }

    function openAddSlot(dayId) {
      addSlotModal.value = { open: true, day: dayId, time: '', action: 1 };
    }

    async function confirmAddSlot() {
      addSlotModal.value.loading = true;
      addSlotModal.value.error   = '';
      try {
        const r = await apiService.setScheduleSlot(props.deviceId, {
          day_of_week: addSlotModal.value.day,
          time:        addSlotModal.value.time,
          action:      addSlotModal.value.action
        });
        slots.value = r.data.slots;
        addSlotModal.value.open = false;
        rafraichirEtat();
      } catch (e) {
        const msg = texteErreur(e, e.message || t('planning.erreurInconnue'));
        addSlotModal.value.error = msg;
        console.error('Erreur addSlot:', e);
      } finally {
        addSlotModal.value.loading = false;
      }
    }

    async function toggleSlot(slot) {
      try {
        const r = await apiService.patchScheduleSlot(props.deviceId, slot.id, { enabled: !slot.enabled });
        slots.value = r.data;
        rafraichirEtat();
      } catch (e) { echec(e); }
    }

    async function removeSlot(slotId) {
      if (!window.confirm(t('planning.confirmerSupprCreneau'))) return;
      try {
        const r = await apiService.deleteScheduleSlot(props.deviceId, slotId);
        slots.value = r.data;
        rafraichirEtat();
      } catch (e) { echec(e); }
    }

    async function addException() {
      erreurException.value = '';
      try {
        const r = await apiService.addScheduleException(props.deviceId, newExc.value);
        exceptions.value = r.data.exceptions;
        showAddException.value = false;
        resetNewExc();
        rafraichirEtat();
      } catch (e) {
        erreurException.value = texteErreur(e, t('planning.erreurInconnue'));
      }
    }

    async function removeException(id) {
      if (!window.confirm(t('planning.confirmerSupprException'))) return;
      try {
        await apiService.deleteScheduleException(props.deviceId, id);
        exceptions.value = exceptions.value.filter(e => e.id !== id);
        rafraichirEtat();
      } catch (e) { echec(e); }
    }

    async function loadPreview() {
      previewLoading.value = true;
      try {
        const r = await apiService.previewSchedule(props.deviceId);
        preview.value = r.data.transitions || [];
      } catch (e) {
        echec(e);
      } finally {
        previewLoading.value = false;
      }
    }

    // Réponse d'« Envoyer » : un code et un texte du serveur (succes.PLANNING_ENVOYE / PLANNING_VIDE,
    // ou une erreur du catalogue). Un envoi vers une carte qui dort est « en attente », pas un échec.
    async function sendToMcu() {
      sending.value    = true;
      sendResult.value = null;
      try {
        const r = await apiService.sendScheduleToDevice(props.deviceId);
        sendResult.value = { ok: true, texte: texteSucces(r.data, t('planning.envoyer')) };
        await rafraichirEtat();
      } catch (e) {
        sendResult.value = { ok: false, texte: texteErreur(e, t('planning.echecEnvoi')) };
      } finally {
        sending.value = false;
      }
    }

    function resetNewExc() {
      erreurException.value = '';
      newExc.value = { label: '', date_from: '', date_to: '', mode: 'armed_all_day', arm_time: '', disarm_time: '' };
    }

    const lastSentLabel = computed(() => {
      if (!config.value.last_sent_at) return t('planning.jamais');
      return new Date(config.value.last_sent_at * 1000).toLocaleString(localeIntl(), { timeZone: fuseau(),
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    });

    // Les heures du planning se lisent dans SON fuseau (un planning en Martinique s'affichait en
    // heure de Paris, décalé de 5 ou 6 h).
    function fuseau() {
      const tz = config.value.timezone || 'Europe/Paris';
      try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return tz; } catch { return 'Europe/Paris'; }
    }
    function formatIso(iso) { return iso ? formatEpoch(Date.parse(iso) / 1000) : '—'; }
    function formatEpoch(ts) {
      if (!ts) return '—';
      return new Date(ts * 1000).toLocaleString(localeIntl(), { timeZone: fuseau(),
        weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
      });
    }

    function modeLabel(mode) {
      return { armed_all_day: t('planning.modeArme'), disarmed_all_day: t('planning.modeDesarme'), custom: t('planning.modePerso'), none: t('planning.modeInchange') }[mode] || mode;
    }

    function modeBadge(mode) {
      const base = 'px-2 py-0.5 rounded text-xs font-medium ';
      return base + ({ armed_all_day: 'bg-orange-100 text-orange-700', disarmed_all_day: 'bg-green-100 text-green-700', custom: 'bg-blue-100 text-blue-700', none: 'bg-gray-100 text-gray-500' }[mode] || 'bg-gray-100 text-gray-500');
    }

    // Temps réel : la carte confirme l'activation (device:schedule_config) ou l'application du
    // planning (device:schedule_updated) — l'en-tête passe de « en attente » à « appliqué ».
    const onEtat = (data) => { if (data.deviceId === props.deviceId) rafraichirEtat(); };
    onMounted(() => {
      loadSchedule();
      const socket = socketService.connect();
      socket.on('device:schedule_config', onEtat);
      socket.on('device:schedule_updated', onEtat);
    });
    onUnmounted(() => {
      const socket = socketService.connect();
      socket.off('device:schedule_config', onEtat);
      socket.off('device:schedule_updated', onEtat);
    });

    return {
      config, slots, exceptions, preview, previewLoading, sending, sendResult,
      showAddException, newExc, addSlotModal, days, timezones,
      slotsForDay, saveConfig, basculer, openAddSlot, confirmAddSlot,
      toggleSlot, removeSlot, addException, removeException,
      loadPreview, sendToMcu, resetNewExc, lastSentLabel, formatEpoch, formatIso, modeLabel, modeBadge,
      erreur, erreurException, activationEnCours, nomFuseau
    };
  }
};
</script>
